import React, { useEffect, useRef, useState } from 'react';
import { AuthContainer, Input, PhoneNumberInput } from '../../../components';
import { notifyError } from '../../../utils/notify';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  Content,
  Form,
  InvitationCodeWrapper,
  LinkText,
  ReviewTextContainer,
  StyledButton,
  Subtitle,
  TextContainer,
  Title,
} from './SignUpPage.styles';
import { EMAIL_VERIFICATION } from '../../../components/App/routes';
import { Text15Light, Text32ExtraBold } from '../../../components/utils/Text/Text.styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { VERIFICATION_CODE_TYPES } from '../../../utils/constants';
import ReCAPTCHA from 'react-google-recaptcha';
import config from '../../../config';
import { createInfluencerRequest, requestVerificationCode } from '../../../store/slices/auth/asyncThunks';
import { selectAuthActionsPending, setSignUpFormData } from '../../../store/slices/auth/slice';
import parsePhoneNumberFromString from 'libphonenumber-js';

export const signUpFormSchema = yup
  .object({
    email: yup.string().trim().required('Email is required').email('Invalid email'),
    name: yup.string().trim().required('Name is required').max(255, 'Max length reached'),
    phoneNumber: yup
      .string()
      .trim()
      .required('Phone Number is required')
      .test('is-valid-phone', 'Phone number is invalid', value => {
        if (!value) return false;
        const phoneNumber = parsePhoneNumberFromString(value);
        return phoneNumber?.isValid() ?? false;
      }),
    instagramUsername: yup.string().trim().required('Instagram username is required').max(255, 'Max length reached'),
  })
  .required();

const SignUpPage = () => {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');
  const {
    requestVerificationCode: requestVerificationCodePending,
    createInfluencerRequest: createInfluencerRequestPending,
  } = useSelector(selectAuthActionsPending);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const recaptchaRef = useRef();
  const [hasInvitationCode, setHasInvitationCode] = useState(false);
  const [sentRequest, setSentRequest] = useState(false);

  const {
    watch,
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: '',
      name: '',
      instagramUsername: '',
      phoneNumber: '',
      twitterUsername: '',
      invitationCode: '',
    },
    delayError: 300,
    resolver: yupResolver(signUpFormSchema),
  });

  const invitationCodeWatch = watch('invitationCode');

  useEffect(() => {
    if (inviteCode) {
      setHasInvitationCode(true);
      setValue('invitationCode', inviteCode);
    }
  }, [inviteCode, setValue]);

  const onSubmit = async data => {
    const { email, invitationCode } = data;
    const recaptchaValue = await recaptchaRef.current.executeAsync();

    if (!invitationCode.trim()) {
      dispatch(
        createInfluencerRequest({
          token: recaptchaValue,
          ...data,
        }),
      )
        .unwrap()
        .then(() => {
          setSentRequest(true);
        })
        .catch(err => {
          recaptchaRef.current.reset();
          notifyError(err.message);
        });
    } else {
      dispatch(
        requestVerificationCode({
          email,
          token: recaptchaValue,
          type: VERIFICATION_CODE_TYPES.EMAIL_VERIFICATION,
          invitationCode,
        }),
      )
        .unwrap()
        .then(() => {
          dispatch(setSignUpFormData(data));
          navigate(`/${EMAIL_VERIFICATION}`, { state: { email, type: VERIFICATION_CODE_TYPES.EMAIL_VERIFICATION } });
        })
        .catch(err => {
          recaptchaRef.current.reset();
          notifyError(err.message);
        });
    }
  };

  return (
    <AuthContainer
      title={!sentRequest && 'Become a Creator'}
      subtitle={
        !sentRequest &&
        'Fill out your information below and our team will reach out to your shortly regarding your approval.'
      }>
      {sentRequest ? (
        <Content>
          <Text32ExtraBold>🎉</Text32ExtraBold>
          <ReviewTextContainer>
            <Title>Your profile is in review.</Title>
            <Subtitle>
              Our team will review your application shortly. We will reach out to you with the next steps once the
              review is complete.
            </Subtitle>
          </ReviewTextContainer>
        </Content>
      ) : (
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Input label="Full Name" placeholder="Enter full name" {...register('name')} error={errors.name?.message} />
          <Input
            label="Email"
            placeholder="Enter your email address"
            {...register('email')}
            error={errors.email?.message}
          />
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => {
              return (
                <PhoneNumberInput
                  label="Phone Number"
                  phone={field.value}
                  setPhone={field.onChange}
                  error={errors.phoneNumber?.message}
                />
              );
            }}
          />
          <Input
            label="Instagram"
            placeholder="Enter your Instagram username"
            {...register('instagramUsername')}
            error={errors.instagramUsername?.message}
          />
          <Input
            label="X (Twitter)"
            placeholder="Enter your X Username"
            {...register('twitterUsername')}
            error={errors.twitterUsername?.message}
          />
          {hasInvitationCode && (
            <InvitationCodeWrapper>
              <Input
                label="Invite Code"
                placeholder="Enter Invite Code"
                {...register('invitationCode')}
                error={errors.invitationCode?.message}
              />
            </InvitationCodeWrapper>
          )}
          <StyledButton
            title={invitationCodeWatch.trim() ? 'Continue' : 'Join the Waitlist'}
            type="submit"
            isLoading={requestVerificationCodePending || createInfluencerRequestPending}
          />
          <TextContainer>
            {!hasInvitationCode && (
              <>
                <Text15Light>Have an invite code?</Text15Light>
                <LinkText onClick={() => setHasInvitationCode(true)}>Skip the waitlist</LinkText>
              </>
            )}
          </TextContainer>
        </Form>
      )}
      <ReCAPTCHA ref={recaptchaRef} sitekey={config.googleRecaptcha.key} size="invisible" />
    </AuthContainer>
  );
};

export default SignUpPage;
